import { date } from '@seek/ca-myprofile-utils';
import type { Language, Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Inline,
  Strong,
  Text,
  useResponsiveValue,
} from 'braid-design-system';
import {
  type ReactNode,
  useEffect,
  useState,
  type ComponentProps,
  type FC,
} from 'react';

import { ApplicationBadgesInOrder } from 'src/App/AppPageBlock/ApplicationBadgesInOrder/ApplicationBadgesInOrder.tsx';
import type { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import type { BUCKETS } from 'src/stores/JobApplicationStore/types';
import buildHandleEnterAndSpaceKeyPress from 'src/utils/buildHandleEnterAndSpaceKeyPress.ts';
import {
  trackCandidateCardStatus,
  trackMatchedQualitiesViewLess,
  trackMatchedQualitiesViewMore,
} from 'src/utils/events';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

import translations from './.vocab/index.ts';
import { AppliedDate } from './AppliedDate/index.ts';
import { AttachmentsAndRecords } from './AttachmentsAndRecords/index.ts';
import { AvatarCheckbox } from './AvatarCheckbox/index.ts';
import { CertsyIdentityVerified } from './CertsyIdentityVerified/index.ts';
import { ChangeStatusButtons } from './ChangeStatusButtons/index.ts';
import { JobApplicationCardLayout } from './JobApplicationCardLayout/index.ts';
import { MatchedQualitiesDisclosure } from './MatchedQualitiesDisclosure/index.ts';
import { ProfileKeyInfo } from './ProfileKeyInfo/index.ts';
import { RoleRequirementsDisclosure } from './RoleRequirementsDisclosure/index.ts';
import { StarRatingButton } from './StarRatingButton.tsx/index.ts';

import * as styles from './JobApplicationCard.css';

export interface JobApplicationCardProps {
  userId: number | null;
  seekAdvertiserId: string;
  jobId: string;
  currentBucket: BUCKETS;
  shouldShowNewBadge: boolean;
  shouldShowRelevantApplicationBadge: boolean;
  shouldShowVerifiedCredentialsBadge: boolean;
  jobApplication: {
    id: string;
    applicationCorrelationId: string;
    candidateId: string;
    fullName: string;
    isUnread: boolean;
    appliedDate: string;
    source: number;
    rating: number;
    seekCandidateId: string;
    applicationId: string;
  };
  lastRole?: {
    from: {
      month: number;
      year: number;
    };
    to?: {
      month: number;
      year: number;
    };
    title: string;
    company: string;
  };
  roleRequirements: ComponentProps<
    typeof RoleRequirementsDisclosure
  >['roleRequirements'];
  applicationListState: 'collapsed' | 'expanded';
  matchedQualities: {
    qualities: Array<{ id: string; displayLabel: string; isTop: boolean }>;
    hasError: boolean;
  };
  certsyVerifiedFullName?: string;
  education?: {
    name: string;
    institute: string;
    completionYear?: number;
  };
  homeLocation?: {
    description: string;
  };
  attachments: Array<{ id: string; type: number; isPdf: boolean }>;
  emailCount: number;
  previousApplicationCount: number;
  notesAndAttachmentsCount: number;
  checkboxProps: {
    checked: boolean;
    onChange: () => void;
  };
  locale: Locale;
  nationalities: string[] | undefined;
  openDetailsDrawer: (
    tab: JOB_APPLICATION_TAB,
    applicationCardIsExpanded?: boolean,
  ) => void;
  openDetailsDrawerProfileTab: (applicationCardIsExpanded?: boolean) => void;
  markApplicationAsRead: () => void;
  changeStatus: (bucket: BUCKETS, isExpanded?: boolean) => Promise<void>;
  onClickRating: (index: number) => number;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  language: Language;
  homeLocationIdsForTracking: string[];
  nationalityIdsForTracking: string[];
  isFilterExpand: boolean;
}

interface DisclosureState {
  roleRequirementsDisclosure:
    | {
        hasRoleRequirements: true;
        isExpanded: boolean;
      }
    | {
        hasRoleRequirements: false;
        isExpanded: undefined;
      };
  matchedQualitiesDisclosure:
    | {
        hasMatchedQualities: true;
        isExpanded: boolean;
      }
    | {
        hasMatchedQualities: false;
        isExpanded: undefined;
      };
}

export const JobApplicationCard: FC<JobApplicationCardProps> = ({
  userId,
  jobId,
  seekAdvertiserId,
  currentBucket,
  shouldShowNewBadge,
  shouldShowRelevantApplicationBadge,
  shouldShowVerifiedCredentialsBadge,
  jobApplication,
  lastRole,
  matchedQualities,
  homeLocation,
  education,
  certsyVerifiedFullName,
  attachments,
  emailCount,
  previousApplicationCount,
  notesAndAttachmentsCount,
  checkboxProps,
  locale,
  applicationListState,
  roleRequirements,
  openDetailsDrawer,
  openDetailsDrawerProfileTab,
  markApplicationAsRead,
  changeStatus,
  onClickRating,
  isVerifiedId,
  nationalities,
  language,
  homeLocationIdsForTracking,
  nationalityIdsForTracking,
  isFilterExpand,
}) => {
  const { t } = useTranslations(translations);

  const responsiveValue = useResponsiveValue();

  const isMobile =
    responsiveValue({
      mobile: true,
      tablet: false,
      desktop: false,
    }) ?? false;

  const [disclosure, setDisclosure] = useState<DisclosureState>(() => ({
    roleRequirementsDisclosure:
      roleRequirements.length > 0
        ? {
            hasRoleRequirements: true,
            isExpanded: applicationListState === 'expanded',
          }
        : {
            hasRoleRequirements: false,
          },
    matchedQualitiesDisclosure:
      matchedQualities.qualities.length > 0
        ? {
            hasMatchedQualities: true,
            isExpanded: applicationListState === 'expanded',
          }
        : {
            hasMatchedQualities: false,
          },
  }));

  const toggleIsExpanded = (type: 'roleRequirements' | 'matchedQualities') => {
    if (
      type === 'roleRequirements' &&
      disclosure.roleRequirementsDisclosure.hasRoleRequirements
    ) {
      const newState = !disclosure.roleRequirementsDisclosure.isExpanded;
      setDisclosure((prev) => ({
        ...prev,
        roleRequirementsDisclosure: {
          hasRoleRequirements: true,
          isExpanded: newState,
        },
      }));
      trackCandidateCardStatus({
        actionOrigin: 'Candidate Listing - Individual Card Change',
        candidateCardStatus: newState ? 'Expand' : 'Collapse',
      });
    }

    if (
      type === 'matchedQualities' &&
      disclosure.matchedQualitiesDisclosure.hasMatchedQualities
    ) {
      const newState = !disclosure.matchedQualitiesDisclosure.isExpanded;
      setDisclosure((prev) => ({
        ...prev,
        matchedQualitiesDisclosure: {
          hasMatchedQualities: true,
          isExpanded: newState,
        },
      }));
      if (disclosure.matchedQualitiesDisclosure.isExpanded) {
        trackMatchedQualitiesViewMore({
          userId,
          jobId,
          seekAdvertiserId,
        });
      } else {
        trackMatchedQualitiesViewLess({
          userId,
          jobId,
          seekAdvertiserId,
        });
      }
    }
  };

  useEffect(() => {
    if (disclosure.roleRequirementsDisclosure.hasRoleRequirements) {
      setDisclosure((prev) => ({
        ...prev,
        roleRequirementsDisclosure: {
          hasRoleRequirements: true,
          isExpanded: applicationListState === 'expanded',
        },
      }));
    }

    if (disclosure.matchedQualitiesDisclosure.hasMatchedQualities) {
      setDisclosure((prev) => ({
        ...prev,
        matchedQualitiesDisclosure: {
          hasMatchedQualities: true,
          isExpanded: applicationListState === 'expanded',
        },
      }));
    }
  }, [
    applicationListState,
    disclosure.roleRequirementsDisclosure.hasRoleRequirements,
    disclosure.matchedQualitiesDisclosure.hasMatchedQualities,
  ]);

  return (
    <Box
      data-hj-masked
      tabIndex={0}
      position="relative"
      cursor="pointer"
      onKeyDown={buildHandleEnterAndSpaceKeyPress(openDetailsDrawerProfileTab)}
      onClick={() => openDetailsDrawerProfileTab()}
    >
      <JobApplicationCardLayout
        avatarCheckBox={
          <AvatarCheckbox
            jobApplication={jobApplication}
            checkboxProps={checkboxProps}
          />
        }
        fullNameAndCertifiedIcon={
          <Inline space="xxsmall" alignY="center">
            <Text weight="strong">{jobApplication.fullName}</Text>
            {certsyVerifiedFullName ? (
              <CertsyIdentityVerified
                idSuffix={jobApplication.id}
                certsyVerifiedFullName={certsyVerifiedFullName}
                boxProps={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            ) : null}
          </Inline>
        }
        recentRoleAndTenure={
          lastRole ? (
            <RecentRoleAndTenure lastRole={lastRole} locale={locale} />
          ) : (
            <Text size="small" weight="strong">
              {t('New to workforce')}
            </Text>
          )
        }
        keyInfo={
          <ProfileKeyInfo
            homeLocation={homeLocation}
            education={education}
            nationalities={nationalities}
            language={language}
          />
        }
        badges={
          <ApplicationBadgesInOrder
            shouldShowNewBadge={shouldShowNewBadge}
            shouldShowRelevantApplicationBadge={
              shouldShowRelevantApplicationBadge
            }
            shouldShowVerifiedCredentialsBadge={
              shouldShowVerifiedCredentialsBadge
            }
            jobApplicationId={jobApplication.id}
            currentBucket={currentBucket}
            language={language}
          />
        }
        roleRequirementsDisclosure={
          disclosure.roleRequirementsDisclosure.hasRoleRequirements ? (
            <RoleRequirementsDisclosure
              roleRequirements={roleRequirements}
              jobApplicationId={jobApplication.id}
              isMobile={isMobile}
              isExpanded={disclosure.roleRequirementsDisclosure.isExpanded}
              toggleIsExpanded={() => toggleIsExpanded('roleRequirements')}
              isFilterExpand={isFilterExpand}
            />
          ) : null
        }
        matchedSkillsDisclosure={
          !matchedQualities.hasError &&
          disclosure.matchedQualitiesDisclosure.hasMatchedQualities ? (
            <MatchedQualitiesDisclosure
              candidateFullname={jobApplication.fullName}
              qualities={matchedQualities.qualities}
              isExpanded={
                disclosure.matchedQualitiesDisclosure.isExpanded ?? false
              }
              toggleIsExpanded={() => toggleIsExpanded('matchedQualities')}
              jobApplicationId={jobApplication.id}
            />
          ) : null
        }
        appliedDate={
          <AppliedDate
            appliedDate={jobApplication.appliedDate}
            source={jobApplication.source}
            locale={locale}
          />
        }
        attachmentsAndRecords={
          attachments ||
          emailCount ||
          previousApplicationCount ||
          notesAndAttachmentsCount ? (
            <AttachmentsAndRecords
              jobApplication={jobApplication}
              isVerifiedId={isVerifiedId}
              isMobile={isMobile}
              attachments={attachments}
              emailCount={emailCount}
              previousApplicationCount={previousApplicationCount}
              notesAndAttachmentsCount={notesAndAttachmentsCount}
              openDetailsDrawer={openDetailsDrawer}
              markApplicationAsRead={markApplicationAsRead}
              homeLocationIdsForTracking={homeLocationIdsForTracking}
              nationalityIdsForTracking={nationalityIdsForTracking}
              jobId={jobId}
            />
          ) : null
        }
        actionButtons={
          <Inline space="xsmall" alignY="center" align={['left', 'right']}>
            <StarRatingButton
              onClickRating={onClickRating}
              rating={jobApplication.rating}
            />
            <ChangeStatusButtons
              jobApplication={jobApplication}
              currentBucket={currentBucket}
              changeStatus={changeStatus}
              isFilterExpand={isFilterExpand}
            />
          </Inline>
        }
      />
      {shouldShowNewBadge ? <Box className={styles.unreadLeftBorder} /> : null}
    </Box>
  );
};

const RecentRoleAndTenure: FC<{
  lastRole: NonNullable<JobApplicationCardProps['lastRole']>;
  locale: JobApplicationCardProps['locale'];
}> = ({ lastRole, locale }) => {
  const [timeSpan, setTimeSpan] = useState('');

  const { t } = useTranslations(translations);
  useEffect(() => {
    date
      .getTimeSpan(
        lastRole.from,
        lastRole.to,
        locale as Parameters<typeof date.getTimeSpan>[2],
      )
      .then((result) => setTimeSpan(result));
  }, [lastRole.from, lastRole.to, locale]);

  const recentRole = (): ReactNode => {
    if (!lastRole.company) {
      return <Strong>{lastRole.title}</Strong>;
    }

    return t('mostRecentJobTitle at mostRecentCompanyName', {
      Strong: (children) => <Strong>{children}</Strong>,
      mostRecentJobTitle: lastRole.title,
      mostRecentCompanyName: lastRole.company,
    });
  };

  return (
    <Inline space="xxsmall" alignY="center">
      <Text size="small">{recentRole()}</Text>
      <Text size="small">({timeSpan})</Text>
    </Inline>
  );
};
